import { StrictMode, useEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import type {
  AutomationPolicySummary,
  AutomationRuleSummary,
  CandidateSummary,
  CatalogPage,
  GroupSummary,
  MissionSummary,
} from "@flowb0t/contracts";
import "./styles.css";

type View =
  | "start"
  | "catalog"
  | "groups"
  | "automation"
  | "missions"
  | "mission";
interface Plan {
  origin: string;
  host: string;
  origins: string[];
  hosts: string[];
  subject: string | null;
  group: { id: number; name: string; category: string } | null;
  catalog: { total: number; highWaterId: number } | null;
  action: string;
  limits: string;
  lanes: string[];
}

function csrfCookie(): string {
  return (
    document.cookie
      .split("; ")
      .find((value) => value.startsWith("fci_csrf="))
      ?.split("=")[1] ?? ""
  );
}

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfCookie(),
      ...(init.headers ?? {}),
    },
  });
  const body = (await response.json().catch(() => ({}))) as T & {
    error?: string;
    detail?: string;
  };
  if (!response.ok)
    throw new Error(
      body.detail ?? body.error ?? `Request failed (${response.status})`,
    );
  return body;
}

function Metric({
  label,
  value,
}: {
  label: string;
  value: MissionSummary["counters"][string] | undefined;
}) {
  return (
    <div className="metric">
      <span>{label}</span>
      <strong>{value?.value ?? "—"}</strong>
      <small>
        {value
          ? `${value.unit} · ${value.scope} · ${new Date(value.asOf).toLocaleTimeString()} · ${value.exactness}`
          : "not measured"}
      </small>
    </div>
  );
}

function App() {
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);
  const [password, setPassword] = useState("");
  const [view, setView] = useState<View>("start");
  const [websites, setWebsites] = useState("");
  const [subject, setSubject] = useState("");
  const [catalogInput, setCatalogInput] = useState("");
  const [catalogCategory, setCatalogCategory] = useState("P4 Proof");
  const [catalogFilter, setCatalogFilter] = useState("");
  const [catalogPage, setCatalogPage] = useState<CatalogPage | null>(null);
  const [catalogAfter, setCatalogAfter] = useState(0);
  const [catalogHistory, setCatalogHistory] = useState<number[]>([]);
  const [selectedCatalog, setSelectedCatalog] = useState<Set<number>>(
    new Set(),
  );
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [groups, setGroups] = useState<GroupSummary[]>([]);
  const [automationRules, setAutomationRules] = useState<
    AutomationRuleSummary[]
  >([]);
  const [automationPolicy, setAutomationPolicy] =
    useState<AutomationPolicySummary | null>(null);
  const [automationSiteId, setAutomationSiteId] = useState("");
  const [automationName, setAutomationName] = useState(
    "Continuous publisher watch",
  );
  const [automationMinSeconds, setAutomationMinSeconds] = useState(30);
  const [automationMaxSeconds, setAutomationMaxSeconds] = useState(3600);
  const [plan, setPlan] = useState<Plan | null>(null);
  const [missions, setMissions] = useState<MissionSummary[]>([]);
  const [selected, setSelected] = useState<MissionSummary | null>(null);
  const [candidates, setCandidates] = useState<CandidateSummary[]>([]);
  const [candidateSelection, setCandidateSelection] = useState<Set<number>>(
    new Set(),
  );
  const [importSiteSelection, setImportSiteSelection] = useState<Set<number>>(
    new Set(),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const refreshing = useRef(false);
  const selectedIdRef = useRef<number | null>(null);
  const viewRef = useRef<View>("start");

  useEffect(() => {
    api<{ authenticated: boolean }>("/api/session")
      .then((session) => setAuthenticated(session.authenticated))
      .catch(() => setAuthenticated(false));
  }, []);
  useEffect(() => {
    selectedIdRef.current = selected?.id ?? null;
  }, [selected?.id]);
  useEffect(() => {
    viewRef.current = view;
  }, [view]);
  const active = useMemo(
    () => selected ?? missions[0] ?? null,
    [selected, missions],
  );

  async function loadMissions() {
    if (refreshing.current) return;
    refreshing.current = true;
    try {
      const next = await api<MissionSummary[]>("/api/missions");
      setMissions(next);
      const currentId = selectedIdRef.current;
      if (currentId) {
        const current = next.find((item) => item.id === currentId);
        if (current && selectedIdRef.current === currentId)
          setSelected(current);
      }
      if (currentId && viewRef.current === "mission") {
        const nextCandidates = await api<CandidateSummary[]>(
          `/api/missions/${currentId}/candidates`,
        );
        if (
          selectedIdRef.current === currentId &&
          viewRef.current === "mission"
        )
          setCandidates(nextCandidates);
      }
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      refreshing.current = false;
    }
  }
  async function loadCatalog(
    after = 0,
    history: number[] = catalogHistory,
    filter = catalogFilter,
  ) {
    const query = new URLSearchParams({
      after: String(after),
      limit: "25",
      ...(filter.trim() ? { category: filter.trim() } : {}),
    });
    try {
      const page = await api<CatalogPage>(`/api/catalog/sites?${query}`);
      setCatalogPage(page);
      if (!automationSiteId && page.sites[0])
        setAutomationSiteId(String(page.sites[0].id));
      setCatalogAfter(after);
      setCatalogHistory(history);
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    }
  }
  async function loadGroups() {
    try {
      setGroups(await api<GroupSummary[]>("/api/groups"));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    }
  }
  async function loadAutomation() {
    try {
      const [rules, policy] = await Promise.all([
        api<AutomationRuleSummary[]>("/api/automation/rules"),
        api<AutomationPolicySummary>("/api/automation/policy"),
      ]);
      setAutomationRules(rules);
      setAutomationPolicy(policy);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    }
  }
  useEffect(() => {
    if (!authenticated) return;
    void loadMissions();
    void loadGroups();
    void loadCatalog(0, []);
    void loadAutomation();
    const timer = window.setInterval(() => {
      void loadMissions();
      if (viewRef.current === "automation") void loadAutomation();
    }, 5_000);
    return () => window.clearInterval(timer);
  }, [authenticated, selected?.id, view]);

  async function login(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api("/api/auth/login", {
        method: "POST",
        body: JSON.stringify({ password }),
      });
      setAuthenticated(true);
      setPassword("");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(false);
    }
  }
  async function reviewPlan(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setPlan(null);
    const targets = websites
      .split(/\r?\n|,/)
      .map((value) => value.trim())
      .filter(Boolean);
    if (!targets.length) {
      setError("Enter at least one website.");
      setBusy(false);
      return;
    }
    if (targets.length > 1 && subject.trim()) {
      setError(
        "Subject search supports one site at a time. Remove the subject or keep one website.",
      );
      setBusy(false);
      return;
    }
    const payload =
      targets.length > 1
        ? { websites: targets }
        : {
            website: targets[0],
            ...(subject.trim() ? { subject: subject.trim() } : {}),
          };
    try {
      setPlan(
        await api<Plan>("/api/plans", {
          method: "POST",
          body: JSON.stringify(payload),
        }),
      );
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(false);
    }
  }
  async function startMission() {
    setBusy(true);
    setError(null);
    try {
      if (!plan) return;
      const payload = plan.catalog
        ? { catalogMode: "all" }
        : plan.group
          ? { groupId: plan.group.id }
          : plan.origins.length > 1
            ? { websites: plan.origins }
            : {
                website: plan.origin,
                ...(plan.subject ? { subject: plan.subject } : {}),
              };
      const mission = await api<MissionSummary>("/api/missions", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      selectedIdRef.current = mission.id;
      viewRef.current = "mission";
      setSelected(mission);
      setImportSiteSelection(new Set(mission.sites.map((site) => site.id)));
      setPlan(null);
      setView("mission");
      await loadMissions();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(false);
    }
  }
  async function addCatalog() {
    const targets = catalogInput
      .split(/\r?\n|,/)
      .map((value) => value.trim())
      .filter(Boolean);
    if (!targets.length) return;
    setBusy(true);
    setError(null);
    try {
      await api("/api/catalog/sites", {
        method: "POST",
        body: JSON.stringify({
          websites: targets,
          category: catalogCategory.trim(),
        }),
      });
      setCatalogInput("");
      setCatalogFilter(catalogCategory.trim());
      setSelectedCatalog(new Set());
      await loadCatalog(0, [], catalogCategory.trim());
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(false);
    }
  }
  async function saveGroup() {
    if (!groupName.trim()) return;
    const selection = selectAllMatching
      ? { mode: "all_matching", category: catalogFilter.trim() }
      : { mode: "explicit", siteIds: [...selectedCatalog] };
    setBusy(true);
    setError(null);
    try {
      await api("/api/groups", {
        method: "POST",
        body: JSON.stringify({
          name: groupName.trim(),
          category: catalogFilter.trim() || catalogCategory.trim(),
          selection,
        }),
      });
      setGroupName("");
      setSelectedCatalog(new Set());
      setSelectAllMatching(false);
      await loadGroups();
      setView("groups");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(false);
    }
  }
  async function reviewGroupPlan(group: GroupSummary) {
    setBusy(true);
    setError(null);
    setPlan(null);
    try {
      setPlan(
        await api<Plan>("/api/plans", {
          method: "POST",
          body: JSON.stringify({ groupId: group.id }),
        }),
      );
      setView("start");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(false);
    }
  }
  async function reviewFullCatalog() {
    setBusy(true);
    setError(null);
    setPlan(null);
    try {
      setPlan(
        await api<Plan>("/api/plans", {
          method: "POST",
          body: JSON.stringify({ catalogMode: "all" }),
        }),
      );
      setView("start");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(false);
    }
  }
  async function createAutomation() {
    if (!automationSiteId || !automationName.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await api<AutomationRuleSummary>("/api/automation/rules", {
        method: "POST",
        body: JSON.stringify({
          catalogSiteId: Number(automationSiteId),
          name: automationName.trim(),
          minIntervalSeconds: automationMinSeconds,
          maxIntervalSeconds: automationMaxSeconds,
        }),
      });
      await loadAutomation();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(false);
    }
  }
  async function controlAutomation(
    id: number,
    action: "pause" | "resume" | "run_now",
  ) {
    setBusy(true);
    setError(null);
    try {
      await api(`/api/automation/rules/${id}/control`, {
        method: "POST",
        body: JSON.stringify({ action }),
      });
      await loadAutomation();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(false);
    }
  }
  async function createPolicyVersion() {
    setBusy(true);
    setError(null);
    try {
      setAutomationPolicy(
        await api<AutomationPolicySummary>("/api/automation/policy", {
          method: "POST",
          body: JSON.stringify({
            changeFactor: 0.5,
            unchangedFactor: 1.5,
            failureFactor: 3,
          }),
        }),
      );
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(false);
    }
  }
  async function rollbackPolicy(version: number) {
    setBusy(true);
    setError(null);
    try {
      setAutomationPolicy(
        await api<AutomationPolicySummary>("/api/automation/policy/rollback", {
          method: "POST",
          body: JSON.stringify({ version }),
        }),
      );
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(false);
    }
  }
  async function openMission(mission: MissionSummary) {
    selectedIdRef.current = mission.id;
    viewRef.current = "mission";
    setSelected(mission);
    setCandidateSelection(new Set());
    setImportSiteSelection(new Set(mission.sites.map((site) => site.id)));
    setCandidates([]);
    setView("mission");
    setError(null);
    try {
      setCandidates(
        await api<CandidateSummary[]>(`/api/missions/${mission.id}/candidates`),
      );
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    }
  }
  async function control(
    lane: "collection" | "import",
    action: "start" | "pause" | "resume",
    siteIds?: number[],
  ) {
    if (!active) return;
    setBusy(true);
    setError(null);
    try {
      setSelected(
        await api<MissionSummary>(`/api/missions/${active.id}/${lane}`, {
          method: "POST",
          body: JSON.stringify({
            action,
            ...(siteIds?.length ? { siteIds } : {}),
          }),
        }),
      );
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(false);
    }
  }
  async function controlSite(
    siteId: number,
    lane: "collection" | "import",
    action: "start" | "pause" | "resume",
  ) {
    if (!active) return;
    setBusy(true);
    setError(null);
    try {
      setSelected(
        await api<MissionSummary>(
          `/api/missions/${active.id}/sites/${siteId}/${lane}`,
          { method: "POST", body: JSON.stringify({ action }) },
        ),
      );
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(false);
    }
  }
  async function decideCandidates(decision: "selected" | "rejected") {
    if (!active || candidateSelection.size === 0) return;
    setBusy(true);
    setError(null);
    try {
      await api(`/api/missions/${active.id}/review`, {
        method: "POST",
        body: JSON.stringify({
          candidateIds: [...candidateSelection],
          decision,
        }),
      });
      setCandidateSelection(new Set());
      setCandidates(
        await api<CandidateSummary[]>(`/api/missions/${active.id}/candidates`),
      );
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(false);
    }
  }

  if (authenticated === null)
    return (
      <main className="center">
        <div className="state-card">
          <div className="spinner" />
          Checking secure session…
        </div>
      </main>
    );
  if (!authenticated)
    return (
      <main className="center">
        <form className="login-card" onSubmit={login}>
          <div className="brand-mark">F</div>
          <p className="eyebrow">FLOWB0T CONTROL PLANE</p>
          <h1>Content Intelligence</h1>
          <p>Clean-room administrative access.</p>
          <label htmlFor="password">Admin password</label>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
          <button disabled={busy || !password}>
            {busy ? "Signing in…" : "Sign in"}
          </button>
          {error && (
            <div role="alert" className="error">
              {error}
            </div>
          )}
        </form>
      </main>
    );

  return (
    <div className="shell">
      <aside>
        <div className="wordmark">
          <div className="brand-mark">F</div>
          <div>
            <strong>Flowb0t</strong>
            <small>Content Intelligence</small>
          </div>
        </div>
        <nav aria-label="Primary">
          <button
            className={view === "start" ? "active" : ""}
            onClick={() => setView("start")}
          >
            Start
          </button>
          <button
            className={view === "catalog" ? "active" : ""}
            onClick={() => setView("catalog")}
          >
            Catalog <span>{catalogPage?.total.value ?? 0}</span>
          </button>
          <button
            className={view === "groups" ? "active" : ""}
            onClick={() => setView("groups")}
          >
            Groups <span>{groups.length}</span>
          </button>
          <button
            className={view === "automation" ? "active" : ""}
            onClick={() => setView("automation")}
          >
            Automation <span>{automationRules.length}</span>
          </button>
          <button
            className={view === "missions" ? "active" : ""}
            onClick={() => setView("missions")}
          >
            Missions <span>{missions.length}</span>
          </button>
          {active && (
            <button
              className={view === "mission" ? "active" : ""}
              onClick={() => setView("mission")}
            >
              Current mission
            </button>
          )}
        </nav>
        <footer>
          Clean-room runtime
          <br />
          <span>Server-owned · durable</span>
        </footer>
      </aside>
      <main className="workspace">
        <header>
          <div>
            <p className="eyebrow">ONE LEDGER · TWO INDEPENDENT LANES</p>
            <h1>
              {view === "start"
                ? "Start a mission"
                : view === "catalog"
                  ? "Site catalog"
                  : view === "groups"
                    ? "Saved groups"
                    : view === "automation"
                      ? "Continuous automation"
                      : view === "missions"
                        ? "Missions"
                        : (active?.name ?? "Mission")}
            </h1>
          </div>
          <div className="health">
            <i /> Runtime connected
          </div>
        </header>
        {error && (
          <div role="alert" className="error banner">
            <strong>Action failed.</strong> {error}
            <button aria-label="Dismiss error" onClick={() => setError(null)}>
              ×
            </button>
          </div>
        )}
        {view === "start" && (
          <section className="start-panel">
            <div className="hero-copy">
              <span className="step">01</span>
              <h2>What should Flowb0t finish?</h2>
              <p>
                Enter one website or a simple one-per-line list. A subject can
                be used with one site. Saved groups can be reviewed from Groups.
                We show the exact route and counting plan before durable
                collection starts.
              </p>
            </div>
            <form onSubmit={reviewPlan} className="composer">
              <div className="composer-fields">
                <div className="field">
                  <label htmlFor="websites">
                    Websites <span>one per line</span>
                  </label>
                  <textarea
                    id="websites"
                    inputMode="url"
                    placeholder={"https://example.com\nhttps://another.example"}
                    value={websites}
                    onChange={(event) => {
                      setWebsites(event.target.value);
                      setPlan(null);
                    }}
                    required={plan?.group === null}
                  />
                </div>
                <div className="field">
                  <label htmlFor="subject">
                    Subject <span>single site only</span>
                  </label>
                  <input
                    id="subject"
                    type="search"
                    placeholder="e.g. supply chain security"
                    value={subject}
                    onChange={(event) => {
                      setSubject(event.target.value);
                      setPlan(null);
                    }}
                    minLength={2}
                    maxLength={500}
                  />
                </div>
                <button disabled={busy}>
                  {busy ? "Analyzing…" : "Review plan"}
                </button>
              </div>
            </form>
            {plan && (
              <article className="plan-card">
                <div className="plan-top">
                  <div>
                    <p className="eyebrow">RECOMMENDED PLAN</p>
                    <h3>
                      {plan.group ? (
                        <>
                          Freeze “{plan.group.name}” · {plan.hosts.length} sites
                        </>
                      ) : plan.subject ? (
                        <>
                          Find “{plan.subject}” on {plan.host}
                        </>
                      ) : plan.hosts.length > 1 ? (
                        <>Finish {plan.hosts.length} independent sites</>
                      ) : (
                        <>Finish {plan.host}</>
                      )}
                    </h3>
                    {plan.group && (
                      <p className="site-plan-list">
                        Category · {plan.group.category}
                      </p>
                    )}
                    {plan.hosts.length > 1 && (
                      <p className="site-plan-list">{plan.hosts.join(" · ")}</p>
                    )}
                  </div>
                  <span className="badge">Ready</span>
                </div>
                <p>{plan.action}</p>
                <div className="lane-preview">
                  {plan.lanes.map((lane, index) => (
                    <div key={lane}>
                      <span>0{index + 1}</span>
                      <strong>{lane}</strong>
                      <small>
                        {index === 0
                          ? plan.subject
                            ? "Discover search, read, prove relevance"
                            : "Discover, read, classify"
                          : "Re-fetch and publish selected posts in parallel"}
                      </small>
                    </div>
                  ))}
                </div>
                <div className="limit-note">
                  <strong>Honest ending</strong>
                  {plan.limits}
                </div>
                <button
                  className="primary wide"
                  disabled={busy}
                  onClick={startMission}
                >
                  Start mission
                </button>
              </article>
            )}
          </section>
        )}
        {view === "catalog" && (
          <section className="catalog-view">
            <div className="catalog-tools">
              <article>
                <p className="eyebrow">ADD PUBLIC SITES</p>
                <h2>Catalog by category</h2>
                <label htmlFor="catalog-sites">
                  Websites <span>one per line</span>
                </label>
                <textarea
                  id="catalog-sites"
                  value={catalogInput}
                  onChange={(event) => setCatalogInput(event.target.value)}
                  placeholder={"https://example.com\nhttps://www.iana.org/"}
                />
                <label htmlFor="catalog-category">Category</label>
                <input
                  id="catalog-category"
                  value={catalogCategory}
                  onChange={(event) => setCatalogCategory(event.target.value)}
                />
                <button
                  className="primary wide"
                  disabled={
                    busy || !catalogInput.trim() || !catalogCategory.trim()
                  }
                  onClick={addCatalog}
                >
                  Add to catalog
                </button>
              </article>
              <article>
                <p className="eyebrow">SAVE CURRENT SELECTION</p>
                <h2>Create a group</h2>
                <label htmlFor="catalog-filter">Category filter</label>
                <div className="inline-fields">
                  <input
                    id="catalog-filter"
                    value={catalogFilter}
                    onChange={(event) => setCatalogFilter(event.target.value)}
                    placeholder="All categories"
                  />
                  <button
                    onClick={() => {
                      setSelectedCatalog(new Set());
                      setSelectAllMatching(false);
                      void loadCatalog(0, [], catalogFilter);
                    }}
                  >
                    Apply
                  </button>
                </div>
                <label className="site-choice">
                  <input
                    type="checkbox"
                    disabled={!catalogFilter.trim()}
                    checked={selectAllMatching}
                    onChange={(event) =>
                      setSelectAllMatching(event.target.checked)
                    }
                  />
                  <span>
                    Select every matching catalog site on the server (
                    {catalogPage?.total.value ?? 0})
                  </span>
                </label>
                <label htmlFor="group-name">Group name</label>
                <input
                  id="group-name"
                  value={groupName}
                  onChange={(event) => setGroupName(event.target.value)}
                  placeholder="e.g. Research publishers"
                />
                <button
                  className="primary wide"
                  disabled={
                    busy ||
                    !groupName.trim() ||
                    (selectAllMatching
                      ? !catalogFilter.trim()
                      : selectedCatalog.size === 0)
                  }
                  onClick={saveGroup}
                >
                  Save group
                </button>
              </article>
            </div>
            <div className="catalog-list-head">
              <div>
                <p className="eyebrow">PAGINATED CATALOG</p>
                <h2>{catalogPage?.total.value ?? 0} exact matching sites</h2>
                <p>
                  Only this page is rendered. “Select every matching” is
                  resolved inside the database.
                </p>
              </div>
              <div className="actions">
                <button
                  disabled={!catalogHistory.length}
                  onClick={() => {
                    const history = [...catalogHistory];
                    const previous = history.pop() ?? 0;
                    void loadCatalog(previous, history);
                  }}
                >
                  Previous
                </button>
                <button
                  disabled={!catalogPage?.nextAfter}
                  onClick={() =>
                    catalogPage?.nextAfter &&
                    void loadCatalog(catalogPage.nextAfter, [
                      ...catalogHistory,
                      catalogAfter,
                    ])
                  }
                >
                  Next
                </button>
              </div>
            </div>
            <div className="catalog-rows">
              {catalogPage?.sites.map((site) => (
                <label
                  key={site.id}
                  className={`catalog-row ${selectedCatalog.has(site.id) ? "selected" : ""}`}
                >
                  <input
                    type="checkbox"
                    disabled={selectAllMatching}
                    checked={selectedCatalog.has(site.id)}
                    onChange={() =>
                      setSelectedCatalog((current) => {
                        const next = new Set(current);
                        next.has(site.id)
                          ? next.delete(site.id)
                          : next.add(site.id);
                        return next;
                      })
                    }
                  />
                  <div>
                    <strong>{site.host}</strong>
                    <small>{site.origin}</small>
                  </div>
                  <span>{site.category}</span>
                </label>
              ))}
            </div>
          </section>
        )}
        {view === "catalog" && (
          <section className="scale-cta">
            <div>
              <p className="eyebrow">FULL CATALOG · STREAMED</p>
              <h2>Run every catalog site without loading every row here</h2>
              <p>
                The server freezes an exact high-water mark, partitions the ID
                range, and materializes only bounded work chunks. Review the
                immutable scope before starting.
              </p>
            </div>
            <button
              className="primary"
              disabled={busy || !catalogPage?.total.value}
              onClick={reviewFullCatalog}
            >
              Review full-catalog mission
            </button>
          </section>
        )}
        {view === "groups" && (
          <section>
            {groups.length === 0 ? (
              <div className="state-card">
                <h2>No saved groups</h2>
                <p>
                  Build one from checked catalog sites or every site matching a
                  category.
                </p>
                <button onClick={() => setView("catalog")}>Open catalog</button>
              </div>
            ) : (
              <div className="group-grid">
                {groups.map((group) => (
                  <article key={group.id}>
                    <p className="eyebrow">{group.category}</p>
                    <h2>{group.name}</h2>
                    <strong>{group.memberCount.value}</strong>
                    <span> saved members · {group.memberCount.scope}</span>
                    <p>
                      Starting a mission freezes current membership, order,
                      category, and tranche assignment.
                    </p>
                    <button
                      className="primary wide"
                      onClick={() => reviewGroupPlan(group)}
                    >
                      Review group mission
                    </button>
                  </article>
                ))}
              </div>
            )}
          </section>
        )}
        {view === "automation" && (
          <section className="automation-view">
            <div className="automation-top">
              <form
                className="automation-create"
                onSubmit={(event) => {
                  event.preventDefault();
                  void createAutomation();
                }}
              >
                <p className="eyebrow">ONE DURABLE SCHEDULER</p>
                <h2>Create a continuous watch</h2>
                <p>
                  Checks use validators first. A real observed change creates a
                  normal, explicitly 25-page-bounded mission in the same ledger;
                  Review and Import stay manual.
                </p>
                <label htmlFor="automation-site">Catalog site</label>
                <select
                  id="automation-site"
                  value={automationSiteId}
                  onChange={(event) => setAutomationSiteId(event.target.value)}
                >
                  <option value="">Choose a site</option>
                  {catalogPage?.sites.map((site) => (
                    <option key={site.id} value={site.id}>
                      {site.host} · {site.category}
                    </option>
                  ))}
                </select>
                <label htmlFor="automation-name">Rule name</label>
                <input
                  id="automation-name"
                  value={automationName}
                  onChange={(event) => setAutomationName(event.target.value)}
                />
                <div className="interval-fields">
                  <label>
                    Minimum seconds
                    <input
                      type="number"
                      min={30}
                      max={86400}
                      value={automationMinSeconds}
                      onChange={(event) =>
                        setAutomationMinSeconds(Number(event.target.value))
                      }
                    />
                  </label>
                  <label>
                    Maximum seconds
                    <input
                      type="number"
                      min={30}
                      max={2592000}
                      value={automationMaxSeconds}
                      onChange={(event) =>
                        setAutomationMaxSeconds(Number(event.target.value))
                      }
                    />
                  </label>
                </div>
                <button
                  className="primary wide"
                  disabled={busy || !automationSiteId || !automationName.trim()}
                >
                  Create continuous watch
                </button>
              </form>
              <article className="policy-card">
                <p className="eyebrow">VERSIONED · REVERSIBLE</p>
                <h2>
                  Cadence policy v{automationPolicy?.activeVersion ?? "—"}
                </h2>
                {automationPolicy?.versions.find(
                  (item) => item.version === automationPolicy.activeVersion,
                ) && (
                  <div className="policy-values">
                    <span>
                      Change ×
                      {
                        automationPolicy.versions.find(
                          (item) =>
                            item.version === automationPolicy.activeVersion,
                        )?.changeFactor
                      }
                    </span>
                    <span>
                      Unchanged ×
                      {
                        automationPolicy.versions.find(
                          (item) =>
                            item.version === automationPolicy.activeVersion,
                        )?.unchangedFactor
                      }
                    </span>
                    <span>
                      Failure ×
                      {
                        automationPolicy.versions.find(
                          (item) =>
                            item.version === automationPolicy.activeVersion,
                        )?.failureFactor
                      }
                    </span>
                  </div>
                )}
                <p>
                  Every check records the exact policy version and old/new
                  cadence. Earlier receipts are never rewritten.
                </p>
                <div className="actions">
                  <button disabled={busy} onClick={createPolicyVersion}>
                    Activate tuned version
                  </button>
                  <button
                    disabled={busy || automationPolicy?.activeVersion === 1}
                    onClick={() => rollbackPolicy(1)}
                  >
                    Roll back to v1
                  </button>
                </div>
                <small>
                  {automationPolicy?.recentEvents[0]
                    ? `Last ${automationPolicy.recentEvents[0].eventType}: v${automationPolicy.recentEvents[0].fromVersion ?? "—"} → v${automationPolicy.recentEvents[0].toVersion}`
                    : "No policy changes yet."}
                </small>
              </article>
            </div>
            {automationRules.length === 0 ? (
              <div className="state-card automation-empty">
                <h2>No continuous watches</h2>
                <p>
                  Create one from an existing clean-room catalog site. The first
                  successful check establishes a baseline.
                </p>
              </div>
            ) : (
              <div className="automation-rules">
                {automationRules.map((rule) => (
                  <article key={rule.id} className="automation-rule">
                    <div className="automation-rule-head">
                      <div>
                        <span className={`status ${rule.state}`}>
                          {rule.state}
                        </span>
                        <h2>{rule.name}</h2>
                        <p>
                          {rule.host} · next{" "}
                          {new Date(rule.nextDueAt).toLocaleString()}
                        </p>
                      </div>
                      <div className="cadence">
                        <strong>{rule.currentIntervalSeconds}s</strong>
                        <span>
                          {rule.minIntervalSeconds}s–{rule.maxIntervalSeconds}s
                        </span>
                      </div>
                    </div>
                    <div className="automation-facts">
                      <span>
                        <b>{rule.lastOutcome ?? "not checked"}</b>
                        <small>last outcome</small>
                      </span>
                      <span>
                        <b>
                          {rule.lastEtag ?? rule.lastModified ?? "not exposed"}
                        </b>
                        <small>validator</small>
                      </span>
                      <span>
                        <b>{rule.consecutiveUnchanged}</b>
                        <small>unchanged streak</small>
                      </span>
                      <span>
                        <b>{rule.lastMissionId ?? "—"}</b>
                        <small>last mission</small>
                      </span>
                    </div>
                    <div className="actions">
                      <button
                        disabled={busy || rule.state === "paused"}
                        onClick={() => controlAutomation(rule.id, "run_now")}
                      >
                        Run now
                      </button>
                      <button
                        disabled={busy}
                        onClick={() =>
                          controlAutomation(
                            rule.id,
                            rule.state === "paused" ? "resume" : "pause",
                          )
                        }
                      >
                        {rule.state === "paused" ? "Resume" : "Pause"}
                      </button>
                    </div>
                    <div className="automation-ledger">
                      <h3>Recent check receipts</h3>
                      {rule.recentChecks.length === 0 ? (
                        <p>No check receipt yet.</p>
                      ) : (
                        rule.recentChecks.map((check) => (
                          <div key={check.id}>
                            <span>{check.outcome}</span>
                            <span>{check.httpStatus ?? "network"}</span>
                            <span>
                              {check.priorIntervalSeconds}s →{" "}
                              {check.nextIntervalSeconds}s
                            </span>
                            <span>policy v{check.policyVersion}</span>
                            <time>
                              {new Date(check.checkedAt).toLocaleTimeString()}
                            </time>
                          </div>
                        ))
                      )}
                    </div>
                    <div className="websub-ledger">
                      <h3>WebSub</h3>
                      {rule.webSub.length === 0 ? (
                        <p>
                          No hub advertised; conditional polling remains active.
                        </p>
                      ) : (
                        rule.webSub.map((subscription) => (
                          <p key={subscription.publicId}>
                            <span className={`status ${subscription.state}`}>
                              {subscription.state}
                            </span>{" "}
                            {subscription.topicUrl}
                            <small>
                              {subscription.lastNotificationAt
                                ? `Last notification ${new Date(subscription.lastNotificationAt).toLocaleString()}`
                                : (subscription.lastError ??
                                  "Waiting for hub verification")}
                            </small>
                          </p>
                        ))
                      )}
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
        )}
        {view === "missions" && (
          <section>
            {missions.length === 0 ? (
              <div className="state-card">
                <h2>No missions yet</h2>
                <p>
                  Start with one website. Empty means no durable mission rows
                  exist.
                </p>
                <button onClick={() => setView("start")}>
                  Start a mission
                </button>
              </div>
            ) : (
              <div className="mission-list">
                {missions.map((mission) => (
                  <button
                    key={mission.id}
                    onClick={() => void openMission(mission)}
                  >
                    <div>
                      <span className={`status ${mission.state}`}>
                        {mission.state}
                      </span>
                      <h3>{mission.name}</h3>
                      <p>{mission.origin}</p>
                    </div>
                    <div className="mission-mini">
                      <strong>{mission.counters.accepted?.value ?? 0}</strong>
                      <span>accepted posts</span>
                    </div>
                    <time>{new Date(mission.updatedAt).toLocaleString()}</time>
                  </button>
                ))}
              </div>
            )}
          </section>
        )}
        {view === "mission" && active && (
          <section className="mission-view">
            <div className="mission-summary">
              <div>
                <span className={`status ${active.state}`}>{active.state}</span>
                {active.subject && (
                  <span className="subject-chip">
                    Subject · {active.subject}
                  </span>
                )}
                {active.scopeType === "batch" && (
                  <span className="subject-chip">
                    Batch · {active.sites.length} sites
                  </span>
                )}
                {active.scopeType === "catalog" && (
                  <span className="subject-chip">
                    Full catalog · exact snapshot
                  </span>
                )}
                <p>
                  {active.scopeType === "batch"
                    ? active.sites.map((site) => site.host).join(" · ")
                    : active.origin}
                </p>
              </div>
              <time>
                Updated {new Date(active.updatedAt).toLocaleTimeString()}
              </time>
            </div>
            <div className="metrics">
              <Metric label="Pages read" value={active.counters.pagesRead} />
              {active.subject && (
                <Metric
                  label="Results declared"
                  value={active.counters.searchDeclaredResults}
                />
              )}
              {active.subject && (
                <Metric
                  label="Search pages served"
                  value={active.counters.searchPagesServed}
                />
              )}
              <Metric
                label="URLs discovered"
                value={active.counters.candidates}
              />
              {active.subject && (
                <Metric
                  label="Relevant posts"
                  value={active.counters.relevantPosts}
                />
              )}
              {active.subject && (
                <Metric
                  label="Irrelevant posts"
                  value={active.counters.irrelevantPosts}
                />
              )}
              <Metric label="Accepted" value={active.counters.accepted} />
              <Metric label="Published" value={active.counters.published} />
              <Metric label="Failures" value={active.counters.failures} />
            </div>
            <div className="lanes">
              <article>
                <div className="lane-head">
                  <div>
                    <p className="eyebrow">LANE 01</p>
                    <h2>Collecting</h2>
                  </div>
                  <span className={`lane-state ${active.collectionState}`}>
                    {active.collectionState}
                  </span>
                </div>
                <p>
                  {active.subject
                    ? "The site’s own search, served result pages, downloaded posts, and a proved ending. Declared results never replace observed pages."
                    : "Routes, pages, candidates, and a proved ending."}{" "}
                  Closing this tab does not stop the server.
                </p>
                <div className="actions">
                  {active.collectionState === "running" ? (
                    <button onClick={() => control("collection", "pause")}>
                      Pause Collection
                    </button>
                  ) : active.collectionState === "paused" ? (
                    <button onClick={() => control("collection", "resume")}>
                      Resume Collection
                    </button>
                  ) : (
                    <span className="terminal-note">
                      Proved terminal · no queued collection work
                    </span>
                  )}
                </div>
              </article>
              <article>
                <div className="lane-head">
                  <div>
                    <p className="eyebrow">LANE 02</p>
                    <h2>Importing</h2>
                  </div>
                  <span className={`lane-state ${active.importState}`}>
                    {active.importState}
                  </span>
                </div>
                <p>
                  Its own cursor re-fetches and validates reviewed posts before
                  publication. Collection continues independently.
                </p>
                <div className="actions">
                  {active.importState === "running" ? (
                    <button onClick={() => control("import", "pause")}>
                      Pause Import
                    </button>
                  ) : (
                    <button
                      className="primary"
                      disabled={
                        !candidates.some(
                          (candidate) =>
                            candidate.state === "import_queued" &&
                            importSiteSelection.has(candidate.siteRunId),
                        )
                      }
                      onClick={() =>
                        control(
                          "import",
                          "start",
                          active.scopeType === "batch"
                            ? [...importSiteSelection]
                            : undefined,
                        )
                      }
                    >
                      Start Import
                    </button>
                  )}
                </div>
              </article>
            </div>
            {active.scopeType === "batch" && (
              <section className="site-panel">
                <div className="review-head">
                  <div>
                    <p className="eyebrow">INDEPENDENT SITE CHILDREN</p>
                    <h2>Per-domain control</h2>
                    <p>
                      Least-recently-claimed fairness, adaptive delay, circuit
                      state, and separate lane controls are durable per site.
                    </p>
                  </div>
                </div>
                <div className="site-grid">
                  {active.sites.map((site) => (
                    <article key={site.id} className="site-card">
                      <label className="site-choice">
                        <input
                          type="checkbox"
                          checked={importSiteSelection.has(site.id)}
                          onChange={() =>
                            setImportSiteSelection((current) => {
                              const next = new Set(current);
                              next.has(site.id)
                                ? next.delete(site.id)
                                : next.add(site.id);
                              return next;
                            })
                          }
                        />
                        <span>Include in batch Import</span>
                      </label>
                      <div className="site-title">
                        <div>
                          <h3>{site.host}</h3>
                          <small>{site.origin}</small>
                        </div>
                        <span className={`lane-state ${site.collectionState}`}>
                          {site.collectionState}
                        </span>
                      </div>
                      <div className="site-metrics">
                        <span>
                          {site.counters.pagesRead?.value ?? 0}
                          <small>read</small>
                        </span>
                        <span>
                          {site.counters.candidates?.value ?? 0}
                          <small>URLs</small>
                        </span>
                        <span>
                          {site.counters.accepted?.value ?? 0}
                          <small>accepted</small>
                        </span>
                        <span>
                          {site.counters.failures?.value ?? 0}
                          <small>failures</small>
                        </span>
                      </div>
                      <p>
                        Rate {site.crawlDelayMs} ms · circuit{" "}
                        {site.circuitState}
                        {site.nextEligibleAt
                          ? ` · next ${new Date(site.nextEligibleAt).toLocaleTimeString()}`
                          : ""}
                      </p>
                      <div className="actions">
                        <button
                          onClick={() =>
                            controlSite(
                              site.id,
                              "collection",
                              site.collectionState === "running"
                                ? "pause"
                                : "resume",
                            )
                          }
                        >
                          {site.collectionState === "running"
                            ? "Pause site"
                            : "Resume site"}
                        </button>
                        {site.importState === "running" ? (
                          <button
                            onClick={() =>
                              controlSite(site.id, "import", "pause")
                            }
                          >
                            Pause site Import
                          </button>
                        ) : (
                          <button
                            disabled={
                              !candidates.some(
                                (candidate) =>
                                  candidate.siteRunId === site.id &&
                                  candidate.state === "import_queued",
                              )
                            }
                            onClick={() =>
                              controlSite(site.id, "import", "start")
                            }
                          >
                            Start site Import
                          </button>
                        )}
                      </div>
                    </article>
                  ))}
                </div>
              </section>
            )}
            {active.scale && (
              <section className="scale-panel">
                <div className="review-head">
                  <div>
                    <p className="eyebrow">STREAMED CATALOG EXECUTION</p>
                    <h2>
                      {active.scale.terminalSites.value ?? 0} of{" "}
                      {active.scale.totalSites.value ?? 0} sites terminal
                    </h2>
                    <p>
                      Frozen through catalog ID{" "}
                      {active.scale.catalogHighWaterId}. The browser receives at
                      most 100 materialized children; the database owns the
                      complete scope.
                    </p>
                  </div>
                  <span
                    className={`scale-pressure ${active.scale.backpressureReason ? "blocked" : "clear"}`}
                  >
                    {active.scale.backpressureReason
                      ? `Backpressure · ${active.scale.backpressureReason.replaceAll("_", " ")}`
                      : "Pressure clear"}
                  </span>
                </div>
                <div className="scale-metrics">
                  <Metric
                    label="Frozen catalog"
                    value={active.scale.totalSites}
                  />
                  <Metric
                    label="Materialized"
                    value={active.scale.materializedSites}
                  />
                  <Metric
                    label="Terminal sites"
                    value={active.scale.terminalSites}
                  />
                  <Metric
                    label="Partitions done"
                    value={active.scale.partitionsCompleted}
                  />
                  <Metric label="Measured rate" value={active.scale.rate} />
                  <Metric label="ETA" value={active.scale.eta} />
                  <Metric
                    label="Database bytes"
                    value={active.scale.databaseBytes}
                  />
                  <Metric
                    label="Storage ceiling"
                    value={active.scale.databaseLimitBytes}
                  />
                </div>
                <div
                  className="partition-track"
                  aria-label="Partition completion"
                >
                  <i
                    style={{
                      width: `${Math.min(100, ((active.scale.partitionsCompleted.value ?? 0) / Math.max(1, active.scale.partitionsTotal.value ?? 1)) * 100)}%`,
                    }}
                  />
                </div>
                <p className="bounded-note">
                  {active.sitesTruncated
                    ? `Showing the first ${active.sites.length} materialized site children. Remaining rows stay server-paginated.`
                    : `Showing all ${active.sites.length} materialized site children.`}
                </p>
              </section>
            )}
            <section className="review-panel">
              <div className="review-head">
                <div>
                  <p className="eyebrow">LIVE REVIEW</p>
                  <h2>Real post candidates</h2>
                  <p>
                    Raw URLs stay in the durable counter while this list shows
                    only downloaded, classified evidence. Only accepted real
                    posts can enter Import.
                  </p>
                </div>
                <div className="actions">
                  <button
                    disabled={busy || !candidateSelection.size}
                    onClick={() => decideCandidates("rejected")}
                  >
                    Reject
                  </button>
                  <button
                    className="primary"
                    disabled={busy || !candidateSelection.size}
                    onClick={() => decideCandidates("selected")}
                  >
                    Select for Import
                  </button>
                </div>
              </div>
              {candidates.length === 0 ? (
                <div className="state-card">
                  <h3>No classified candidates yet</h3>
                  <p>
                    {(active.counters.candidates?.value ?? 0) > 0
                      ? `${active.counters.candidates?.value} raw URLs are queued or being classified. They will appear here only with real page evidence.`
                      : "The worker has not discovered a candidate URL for this mission yet."}
                  </p>
                </div>
              ) : (
                <div className="candidate-list">
                  {candidates.map((candidate) => {
                    const selectable = candidate.state === "accepted";
                    return (
                      <label
                        key={candidate.id}
                        className={`candidate ${candidateSelection.has(candidate.id) ? "selected" : ""} ${!selectable ? "disabled" : ""}`}
                      >
                        <input
                          type="checkbox"
                          disabled={!selectable}
                          checked={candidateSelection.has(candidate.id)}
                          onChange={() =>
                            setCandidateSelection((current) => {
                              const next = new Set(current);
                              next.has(candidate.id)
                                ? next.delete(candidate.id)
                                : next.add(candidate.id);
                              return next;
                            })
                          }
                        />
                        <div>
                          <div className="candidate-top">
                            <span className={`status ${candidate.state}`}>
                              {candidate.state}
                            </span>
                            <span>{candidate.siteHost}</span>
                            <span>{candidate.reviewState}</span>
                            <span>
                              {candidate.confidence === null
                                ? "unscored"
                                : `${Math.round(candidate.confidence * 100)}% post confidence`}
                            </span>
                            {candidate.subjectMatch !== null && (
                              <span
                                className={
                                  candidate.subjectMatch
                                    ? "relevant"
                                    : "irrelevant"
                                }
                              >
                                {candidate.subjectMatch
                                  ? `${Math.round((candidate.relevanceScore ?? 0) * 100)}% subject evidence`
                                  : "subject rejected"}
                              </span>
                            )}
                          </div>
                          <h3>{candidate.title ?? candidate.url}</h3>
                          <p>{candidate.description ?? candidate.url}</p>
                          {candidate.rejectionReason && (
                            <p className="reason">
                              Reason:{" "}
                              {candidate.rejectionReason.replaceAll("_", " ")}
                            </p>
                          )}
                          <small>
                            {candidate.author ?? "Author not exposed"} ·{" "}
                            {candidate.publishedAt
                              ? new Date(
                                  candidate.publishedAt,
                                ).toLocaleDateString()
                              : "Date not exposed"}{" "}
                            · {candidate.tags.join(", ") || "No tags exposed"}
                          </small>
                          {candidate.subjectEvidence.length > 0 && (
                            <small className="subject-evidence">
                              {candidate.subjectEvidence.join(" · ")}
                            </small>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </section>
            <details>
              <summary>Advanced evidence</summary>
              <pre>{JSON.stringify(active, null, 2)}</pre>
            </details>
          </section>
        )}
        {view === "mission" && active?.selectionSource === "group" && (
          <section className="snapshot-panel">
            <div className="review-head">
              <div>
                <p className="eyebrow">IMMUTABLE SELECTION SNAPSHOT</p>
                <h2>{active.selectedGroup?.name}</h2>
                <p>
                  {active.selectedGroup?.category} · frozen at mission creation
                  · tranche size {active.trancheSize}
                </p>
              </div>
              <div className="snapshot-total">
                <strong>
                  {active.counters.tranchesCompleted?.value ?? 0}/
                  {active.counters.tranchesTotal?.value ?? 0}
                </strong>
                <span>tranches complete</span>
              </div>
            </div>
            <div className="snapshot-rows">
              {active.sites.map((site) => (
                <div key={site.id}>
                  <span>#{site.selectionOrdinal}</span>
                  <strong>{site.host}</strong>
                  <small>{site.category}</small>
                  <b>
                    Tranche {site.trancheIndex} · {site.collectionState}
                  </b>
                  <code>{site.snapshotHash.slice(0, 12)}</code>
                </div>
              ))}
            </div>
          </section>
        )}
        {view === "mission" && !active && (
          <div className="state-card">
            <h2>No mission selected</h2>
            <p>Create a mission or select an existing one.</p>
            <button onClick={() => setView("start")}>Start</button>
          </div>
        )}
      </main>
    </div>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
